export interface MinVersions {
  minIosVersion: string;
  minAndroidVersion: string;
  // minWebVersion: string;
}

export class MinVersionsFetchError extends Error {
  type: "network" | "http" | "unknown";
  status?: number;

  constructor(type: "network" | "http" | "unknown", message: string, status?: number) {
    super(message);
    this.name = "MinVersionsFetchError";
    this.type = type;
    this.status = status;
  }
}

// Strip diacritics so we can match "conexión" / "connexion" Style strings without language-specific branching.
function foldForConnectivityMatch(raw: string): string {
  return raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

/** True when navigator reports offline (best-effort; not guaranteed for all transports). */
function looksOfflineViaNavigator(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Classify probable connectivity failures for fetch()-level errors.
 * fetch() rejects with TypeError/DOMException in most engines on network failure; messages vary by OS, WebView and locale.
 */
function isConnectivityFetchFailure(error: unknown): boolean {
  if (looksOfflineViaNavigator()) {
    return true;
  }

  if (error instanceof TypeError) {
    return true;
  }

  if (error instanceof DOMException) {
    // AbortError covers client timeouts via AbortSignal; acceptable to treat as transient connectivity UX.
    // Do not classify SecurityError as network (often mixed content / cert / policy unrelated to ISP reachability).
    if (error.name === "AbortError" || error.name === "NetworkError") {
      return true;
    }
    return foldForConnectivityMatch(error.message || "").includes("network");
  }

  const text = typeof error === "object" && error !== null && "message" in error && typeof (error as Error).message === "string"
    ? (error as Error).message
    : String(error ?? "");

  const m = foldForConnectivityMatch(text);
  // Spanish/Portuguese "network" as a standalone word (avoid substring matches like "credito").
  if (/\bred\b/.test(m)) {
    return true;
  }
  // English + Romance-language hints commonly seen from WebViews (not exhaustive; complements TypeError path).
  const hints = [
    "network",
    "internet",
    "offline",
    "timeout",
    "unreachable",
    "failed to fetch",
    "load failed",
    "fetch",
    // Spanish / Portuguese (accent-folded)
    "conexion",
    "sin internet",
    "sem internet",
    "desconect",
    "rechaz",
    "no se encontr",
    "nao encontr",
    "certificado",
    "certificate",
    "ssl",
  ];

  return hints.some((needle) => m.includes(foldForConnectivityMatch(needle))) || /\bdns\b/.test(m);
}

// function to get the minimum versions
export const getMinVersions = async (): Promise<MinVersions> => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/app-config/min-versions`;

  try {
    // Using native fetch for configuration data (no caching needed)
    let response: Response;
    try {
      response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          // No authorization needed since this endpoint is public
        },
        credentials: "include",
      });
    } catch (error) {
      console.error("Error fetching minimum versions (fetch):", error);
      throw new MinVersionsFetchError(
        isConnectivityFetchFailure(error) ? "network" : "unknown",
        `Failed to fetch minimum versions: ${error instanceof Error ? error.message : String(error)}`,
      );
    }

    if (!response.ok) {
      throw new MinVersionsFetchError("http", `Failed to fetch minimum versions: ${response.status}`, response.status);
    }

    try {
      return await response.json();
    } catch (error) {
      console.error("Error fetching minimum versions (JSON):", error);
      throw new MinVersionsFetchError("unknown", `Invalid min-versions payload: ${error instanceof Error ? error.message : String(error)}`);
    }
  } catch (error) {
    console.error("Error fetching minimum versions:", error);

    if (error instanceof MinVersionsFetchError) {
      throw error;
    }

    const message = error instanceof Error ? error.message : "Unknown error";

    throw new MinVersionsFetchError(
      isConnectivityFetchFailure(error) ? "network" : "unknown",
      `Failed to fetch minimum versions: ${message}`,
    );
  }
};
